import { server } from "../../config/ConfigFirebase";
import { getToken } from "../auth/AuthServices";

// Informes de usuarios: the `userReports` Cloud Function computes the
// dashboard and the exported list over every user, and enforces
// user_admin.manage from the caller's token.
//
// Unlike most services these answer { body, error } rather than null on
// error: the page must tell "you may not see this" (FORBIDDEN) apart from a
// failure (FAILED). A 403 is a real case, not a bug: the frontend unions live
// role permissions while the backend reads the stored snapshot.

const userReportsUrl = () =>
  process.env.REACT_APP_USER_REPORTS_URL || `https://${server}.cloudfunctions.net/userReports`;

const KNOWN_ERRORS = ["NAME_TAKEN", "NOT_FOUND", "TOO_MANY"];

const callUserReports = async (body) => {
  try {
    const token = await getToken();
    const resp = await fetch(userReportsUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(body),
    });
    if (resp.status === 401 || resp.status === 403) return { body: null, error: "FORBIDDEN" };
    const json = await resp.json().catch(() => null);
    // Codes a page acts on (a saved report's name already used, or deleted
    // by someone else meanwhile); anything else is a failure.
    if (KNOWN_ERRORS.includes(json?.error)) return { body: null, error: json.error };
    if (!resp.ok || !json?.result) return { body: null, error: "FAILED" };
    return { body: json.body, error: null };
  } catch (e) {
    console.error(`userReports ${body.action} error:`, e);
    return { body: null, error: "FAILED" };
  }
};

// `from`/`to` are optional ISO strings; omit them for the whole history. The
// rest narrows the dashboard as it narrows the report: dependencies, roles,
// roleMatch, accountOnly, exclusions.
export const getUserStats = ({ from, to, ...scope } = {}) =>
  callUserReports({ action: "STATS", ...scope, from: from || null, to: to || null });

// What the filter and exclusion pickers offer, over every user, and the saved
// reports: { dependencies, values: { level, position, city, entity, usertitle,
// emailDomain }, users, savedReports }.
export const getUserReportOptions = () => callUserReports({ action: "OPTIONS" });

// Saved reports, shared in config/userReports. `report` holds a name, the
// filters, the exclusions and the builder settings; with `id` it replaces that
// one. body: { saved, savedReports }; error may be NAME_TAKEN / NOT_FOUND / TOO_MANY.
export const saveUserReport = (report) => callUserReports({ action: "SAVE_REPORT", report });

// body: { savedReports }.
export const deleteUserReport = (id) => callUserReports({ action: "DELETE_REPORT", id });

// filters: { status, dependencies, roles, roleMatch, changes: { from, to } | null,
//            exclusions: { rules, emailDomains, users } }
// body: { columns, rows, total, excluded, changes, findings }.
export const previewUserReport = (filters) =>
  callUserReports({ action: "REPORT", format: "json", ...filters });

// Builds the .xlsx on the server and saves it. Returns null when downloaded,
// else the error code.
export const downloadUserReportXlsx = async (filters) => {
  const { body, error } = await callUserReports({ action: "REPORT", format: "xlsx", ...filters });
  if (error || !body?.base64) return error || "FAILED";
  const bytes = Uint8Array.from(atob(body.base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = body.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked a moment later: revoking right after click() can cancel the
  // download before the browser has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return null;
};
